/**
 * The collection-policy slice of the wire — Task U15 (Tier-3 live-tenant readiness),
 * consuming `GET`/`POST /api/v1/policies`, `PATCH /api/v1/policies/{id}`,
 * `POST /api/v1/policies/{id}/activate` and the two rollout reads
 * (`src/modules/policies/admin-routes.ts`, `openapi.yaml`'s `Policy`,
 * `PolicyRollout` and `PolicyRolloutDevice` schemas).
 *
 * Hand-written to mirror the contract, the same discipline `retention.ts` and
 * `permission-grants.ts` state: transcribe what the screen reads, not the whole
 * document.
 *
 * ── The precondition is `etag`, and it is NOT `updated_at` ──────────────────
 *
 * Both fields are on every `Policy` and they are not interchangeable. `etag` is
 * `updated_at` rendered by PostgreSQL to the **microsecond**; `updated_at` is the
 * same instant through a JavaScript `Date`, three digits short, and `ifMatchToken`
 * (`admin-routes.ts`) refuses it with a `400`. Until U15 the list carried only the
 * second, which is why a screen driven by the list could not write at all.
 *
 * `etag` is typed `string` and not `string | null`: the server computes it for every
 * row of every read and write, so a version without one is not a version whose
 * concurrency is unknown — it is a response this build does not understand. The
 * surface still routes it through `preconditionOf`, which refuses a write when the
 * token is missing, because that is the hook's rule and not this file's.
 *
 * ── Two reads for the rollout, because they need two permissions ────────────
 *
 * `GET /policies/{id}/rollout` is `workforce.manage_policies` and answers with
 * counts; `GET /policies/{id}/rollout/devices` additionally needs
 * `workforce.manage_devices` and answers with hostnames. The codes are independent
 * in `src/lib/permissions.ts`, so the screen asks for the second only when the
 * session reports holding it — and a `403` is still rendered rather than swallowed,
 * because the menu is not a security boundary and neither is a client-side `if`.
 */

export const POLICIES_PATH = '/api/v1/policies';

export const policyPath = (id: string): string => `${POLICIES_PATH}/${id}`;
export const activatePolicyPath = (id: string): string => `${POLICIES_PATH}/${id}/activate`;
export const policyRolloutPath = (id: string): string => `${POLICIES_PATH}/${id}/rollout`;
export const policyRolloutDevicesPath = (id: string): string =>
  `${POLICIES_PATH}/${id}/rollout/devices`;

/** `Policy.status` of `openapi.yaml`. */
export type PolicyStatus = 'draft' | 'active' | 'archived';

/**
 * `Policy`, narrowed to what this screen reads.
 *
 * The private lists, the redaction patterns and the blocked lists are deliberately
 * **not** here. They are on the wire and this build ships no editor for them: they
 * are arrays whose entries are screened at the server for catastrophic backtracking,
 * and a text box that splits on commas is how a company loses every window title it
 * collects. `PolicyEditorScreen` says so on screen rather than offering a control
 * that would be wrong in the one place being wrong matters most.
 */
export interface Policy {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly policy_version: string;
  readonly version_number: number;
  readonly status: PolicyStatus;
  readonly is_default: boolean;
  readonly idle_short_threshold_seconds: number;
  readonly idle_extended_threshold_seconds: number;
  readonly collection_level: number;
  readonly collect_window_title: boolean;
  readonly collect_off_hours: boolean;
  readonly screenshot_enabled: boolean;
  readonly screenshot_lawful_basis: string | null;
  readonly keylog_enabled: boolean;
  readonly usb_monitoring_enabled: boolean;
  readonly file_monitoring_enabled: boolean;
  readonly merge_gap_seconds: number;
  readonly published_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /** The compare-and-set token, unquoted. Send as `If-Match: "<etag>"`. */
  readonly etag: string;
}

export interface PolicyListResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly data: readonly Policy[];
}

/**
 * The settings this screen edits.
 *
 * `keylog_enabled` is absent and always will be: `wp_policies_privacy_ck` refuses
 * `true` at the database and the API refuses it with a `400`, so a control here
 * would be one that cannot succeed. Absent, not disabled — the codebase's rule.
 */
export interface PolicyDraft {
  readonly name: string;
  readonly collection_level: number;
  readonly idle_short_threshold_seconds: number;
  readonly idle_extended_threshold_seconds: number;
  readonly collect_window_title: boolean;
  readonly collect_off_hours: boolean;
  readonly screenshot_enabled: boolean;
  readonly screenshot_lawful_basis: string | null;
  readonly usb_monitoring_enabled: boolean;
  readonly file_monitoring_enabled: boolean;
}

/**
 * `PolicyCreateRequest`. `name` is the only required member, and it is the only one
 * this type makes non-optional — every other setting has a column default, and a
 * create body that filled them in from the client would be this screen deciding a
 * company's surveillance posture on its behalf.
 */
export interface PolicyCreateRequest {
  readonly name: string;
  readonly collection_level?: number;
  readonly idle_short_threshold_seconds?: number;
  readonly idle_extended_threshold_seconds?: number;
  readonly collect_window_title?: boolean;
  readonly collect_off_hours?: boolean;
  readonly screenshot_enabled?: boolean;
  readonly screenshot_lawful_basis?: string | null;
  readonly usb_monitoring_enabled?: boolean;
  readonly file_monitoring_enabled?: boolean;
}

/** `PolicyRollout` — the four counts, what they were counted against, and when. */
export interface PolicyRollout {
  readonly policy_id: string;
  readonly policy_version: string;
  readonly status: PolicyStatus;
  readonly is_default: boolean;
  readonly acknowledged: number;
  readonly pending: number;
  readonly stale: number;
  readonly offline: number;
  /** Every machine in the population — the four counts need not sum to it. */
  readonly total: number;
  readonly as_of: string;
}

export type DeviceLiveness = 'online' | 'stale' | 'offline';

/** `PolicyRolloutDevice`. No employee id and no name — see the endpoint. */
export interface PolicyRolloutDevice {
  readonly device_id: string;
  readonly hostname: string;
  readonly status: string;
  readonly liveness: DeviceLiveness;
  readonly last_heartbeat_at: string | null;
  readonly current_policy_version: string | null;
  readonly acked_policy_version: string | null;
  readonly policy_acked_at: string | null;
}

export interface PolicyRolloutDeviceListResponse {
  readonly total: number;
  readonly offset: number;
  readonly limit: number;
  readonly data: readonly PolicyRolloutDevice[];
}

/**
 * The three collection levels of `wp_policies.collection_level`.
 *
 * A closed list, so a fourth level added by a later migration is a compile error at
 * the label map rather than a silently unrendered option — the rule
 * `PoliciesScreen`'s category vocabulary states for the same reason.
 */
export const COLLECTION_LEVELS = [1, 2, 3] as const;

export type CollectionLevel = (typeof COLLECTION_LEVELS)[number];
